<template>
    <div class="CriterionSettings">
        <CriterionInformationBanner
            :template="template"
            :criterion="criterion"
            :custom-properties="customProperties"
        />
        <div v-if="orderedTemplateReferences.length > 0" class="CriterionSettings__criterionFields">
            <div
                class="CriterionSettings__table"
                :class="{'CriterionSettings__table--hasSubstitutable': hasSubstitutableReference}"
            >
                <CriterionSettingsDescription :template="template" />
                <DynamicCriterionSetting
                    class="CriterionSettings__tableBody"
                    :template="template"
                    :criterion="criterion"
                    :custom-properties="customProperties"
                    :load-criterion-message="loadCriterionMessage"
                    :show-validation-errors="showValidationErrors"
                    @input="onInput"
                    @reference-accessed="onReferenceAccessed"
                    @controller-value-update="controllerValueUpdate"
                    @update:visible-schemas="onUpdateVisibleSchemas"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import {
        JsonSchemaCustomForm,
        JsonSchemaCustomFormElement,
    } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import { FormController } from '@evidentid/json-schema/FormController';
    import { JsonFormObject } from '@evidentid/json-schema/interfaces/JsonForm';
    import { JsonSchemaObject } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import CriterionEmailMessage
        from '@/modules/decisioning-criteria/components/CriterionEmailMessage/CriterionEmailMessage.vue';
    import CriterionInformationBanner
        from '@/modules/decisioning-criteria/components/CriterionInformationBanner/CriterionInformationBanner.vue';
    import CriterionSettingsDescription
        from '@/modules/decisioning-criteria/components/CriterionSettingsDescription/CriterionSettingsDescription.vue';
    import { fieldsUseNewJsonForm } from '@/modules/decisioning-criteria/components/DynamicCriterionSetting/constants';
    import DynamicCriterionSetting
        from '@/modules/decisioning-criteria/components/DynamicCriterionSetting/DynamicCriterionSetting.vue';
    import GeneralAggregateLimitBreakdownBanner
        from '@/modules/decisioning-criteria/components/GeneralAggregateLimitBreakdownBanner/GeneralAggregateLimitBreakdownBanner.vue';
    import PublicProductsLiabilityCriterion
        from '@/modules/decisioning-criteria/components/PublicProductsLiabilityCriterion/PublicProductsLiabilityCriterion.vue';
    import RiskProfileCriteriaTable
        from '@/modules/decisioning-criteria/components/RiskProfileCriteriaTable/RiskProfileCriteriaTable.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { buildCriterionSchema } from '@/modules/decisioning-criteria/schemas/buildCriterionSchema';
    import { CriterionMessageStatus, useDecisioningCriteriaStore } from '@/modules/decisioning-criteria/store';
    import { eidStandardizeCriterionSchema } from '@/modules/decisioning-criteria/utils/eidStandardizeCriterionSchema';
    import { getAllSubstitutableSchemas } from '@/modules/decisioning-criteria/utils/getAllSubstitutableSchemas';
    import { isSubstitutableSchema } from '@/modules/decisioning-criteria/utils/isSubstitutableSchema';
    import {
        isSubstitutableSchemaWithValue,
    } from '@/modules/decisioning-criteria/utils/schemaHaveSubstitutableSchemaWithValue';

    @Component({
        emits: [ 'input', 'reference-accessed' ],
        components: {
            PublicProductsLiabilityCriterion,
            CriterionEmailMessage,
            CriterionInformationBanner,
            CriterionSettingsDescription,
            RiskProfileCriteriaTable,
            DynamicCriterionSetting,
            GeneralAggregateLimitBreakdownBanner,
            JsonSchemaCustomForm,
            JsonSchemaCustomFormElement,
        },
    })
    export default class CriterionSettings extends Vue {
        @Prop({ type: Object, default: () => ({}) })
        private criterion!: EditableRequirementCriterion;

        @Prop({ type: Object, default: () => ({}) })
        private template!: CriterionTemplate;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: String, default: 'US' })
        private countryCode!: string;

        @Prop({ type: Boolean, default: false })
        private showValidationErrors!: boolean;

        private criterionCurrentValue: any | null = null;
        private messageDebounceTimer: any = null;
        private visibleSchemaPaths: string[] = [];
        private substitutableSchemas: Record<string, boolean> = {};

        private get rpName(): string {
            return this.$route.params.rpId as string;
        }

        private get orderedTemplateReferences(): string[] {
            return Object.keys(this.template.evaluator.references) || [];
        }

        private get hasSubstitutableReference(): boolean {
            if (!this.customProperties.length) {
                return false;
            }
            if (this.isNewJsonSchemaForm) {
                return this.visibleSchemaPaths.some((schemaPath) => this.substitutableSchemas[schemaPath]);
            }
            return Object.entries(this.template.evaluator.references).some(
                ([ key, templateRef ]) => {
                    if (templateRef.schema && isSubstitutableSchema(templateRef.schema)) {
                        const fieldIsNotVisible = this.criterionCurrentValue &&
                            !Object.keys(this.criterionCurrentValue).includes(key);
                        return !fieldIsNotVisible;
                    }

                    return false;
                },
            );
        }

        private get isNewJsonSchemaForm(): boolean {
            return fieldsUseNewJsonForm.includes(this.template.field);
        }

        private get criterionMessageStatus(): CriterionMessageStatus {
            const criterionId = 'id' in this.criterion && this.criterion.id
                ? this.criterion.id
                : `${this.criterion.coverageType}-${this.criterion.field}`;
            const messagesPerRp = useDecisioningCriteriaStore().criterionMessage[this.rpName];
            const messagePerCriterion = messagesPerRp ? messagesPerRp[criterionId] : null;
            return messagePerCriterion || { status: OperationStatus.uninitialized, data: null };
        }

        private onInput(updatedCriterion: EditableRequirementCriterion): void {
            this.$emit('input', updatedCriterion);
        }

        private onReferenceAccessed(data: { reference: string, isValid: boolean }): void {
            this.$emit('reference-accessed', data);
        }

        private controllerValueUpdate(controllerValue: any): void {
            this.criterionCurrentValue = { ...controllerValue };
        }

        // @Deprecated: currently no longer used, however, keeping it here as it's likely we will need the feature back
        private loadCriterionMessageLegacy(
            controllerOrIsFormValid: FormController | boolean,
            updatedCriterion: EditableRequirementCriterion,
        ) {
            if (typeof controllerOrIsFormValid === 'boolean') {
                if (!controllerOrIsFormValid) {
                    return;
                }
            } else if (!this.isFormValid(controllerOrIsFormValid)) {
                return;
            }
            if (this.criterionMessageStatus.status !== OperationStatus.loading) {
                clearTimeout(this.messageDebounceTimer);
                this.messageDebounceTimer = setTimeout(() => {
                    useDecisioningCriteriaStore().loadCriterionMessage({
                        rpName: this.rpName,
                        criterion: updatedCriterion,
                        countryCode: this.countryCode,
                    });
                }, 100);
            }
        }
        /*
         * @Deprecated: Temporarily maintained as a no-op function to preserve API compatibility.
         * This method previously handled criterion validation messages but the feature has been disabled.
         * Keeping the method signature to prevent breaking downstream dependencies until removal is confirmed.
         */
        private loadCriterionMessage(
            controllerOrIsFormValid: FormController | boolean,
            updatedCriterion: EditableRequirementCriterion,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
        ): void {}

        // TODO (PRODUCT-17188): no need to check conditionalRef once BE move them to schema with if else then
        private isFormValid(controller: FormController): boolean {
            const conditionalRefs = this.template.displayMetadata.conditionalValueReference;
            if (!conditionalRefs) {
                return controller.valid || false;
            }
            const hiddenRef = controller.value[conditionalRefs.flagValueReference]
                ? conditionalRefs.flagDisabledReference
                : conditionalRefs.flagEnabledReference;
            return (controller.form as JsonFormObject).getProperties()
                .every((property) =>
                    (property.name === hiddenRef
                        ? true
                        : property.form.isValid(controller.value[property.name], true)),
                );
        }

        private onUpdateVisibleSchemas(visibleSchemas: string[]) {
            this.visibleSchemaPaths = visibleSchemas;
        }

        private beforeMount() {
            const substitutableSchemas = getAllSubstitutableSchemas(
                eidStandardizeCriterionSchema(
                    buildCriterionSchema(
                        this.criterion.references,
                        Object.keys(this.template.evaluator.references) || [],
                        this.template,
                    ),
                    this.customProperties,
                ) as JsonSchemaObject,
            );
            const onlySubstitutableSchemasWithValues = substitutableSchemas.reduce((acc, { schemaPath, schema }) => {
                if (isSubstitutableSchemaWithValue(schema)) {
                    acc[schemaPath] = true;
                }
                return acc;
            }, {} as Record<string, boolean>);
            this.substitutableSchemas = onlySubstitutableSchemasWithValues;
        }
    }
</script>
